/**
 * `session` —— 解锁后的「一次装配」引导（T04 关键接线点）。
 *
 * 职责：
 *  - 创建 `RelayService` 并启动传输层（libp2p 节点 + 中继连接）；
 *  - 把 `HybridTransport` 注入容器（`setTransport`），供 `ChatService` 使用；
 *  - 注入 `Libp2pBrowserPeer`（`setPeerClient`）与 `RelayContentStore`（`setContentStore`）；
 *  - 启动 `ChatService` 并让 `FeedService` 订阅广场实时流。
 *
 * 幂等：`startSession` 只在首次解锁时真正装配；`stopSession` 在锁定时停止传输、清内存态，
 * 但保留 identity / storage / chain，便于再次解锁时快速重建。
 */

import { container } from './container';
import { RelayService } from './RelayService';
import { RelayContentStore } from '@/content/RelayContentStore';
import { createLogger } from '@/lib/logger';
import { checkCircleNotices, buildFriendDids } from '@/features/circle/circleFriends';
import { useChatStore } from '@/store/chat';
import { useSocialStore } from '@/store/social';
import { useIdentityStore } from '@/store/identity';

const logger = createLogger('service:session');

let relay: RelayService | null = null;
let active = false;

/**
 * 供 UI 取得当前 `RelayService` 句柄（未装配时为 `null`）。
 *
 * 用途：「切换中继」动作（`errorCatalog['E_RELAY_UNAVAILABLE'].action === 'switchRelay'`）此前
 * 无任何 UI 可达路径 —— `relay` 是模块级 `let` 且未导出。此 getter 补齐该句柄。
 */
export function getRelayService(): RelayService | null {
  return relay;
}

/** 解锁后调用：装配传输层 + 内容存储 + Feed/Chat 服务，并订阅广场实时流。幂等。 */
export async function startSession(): Promise<void> {
  if (active) return;
  if (!relay) {
    relay = new RelayService({ storage: container.storage, identity: container.identity });
  }
  const transport = await relay.start();
  if (!container.hasTransport()) container.setTransport(transport);
  if (!container.hasPeerClient()) container.setPeerClient(transport.getPeerClient());
  if (!container.hasContentStore()) {
    container.setContentStore(new RelayContentStore(transport.getPeerClient()));
  }
  // 把 chat 回注给 relay：中继（含重连）就绪时由 `handleRelayReady` 触发补发未握手 intro。
  // 构造顺序上 relay 先于 chat（chat 依赖已装配的 transport），故此处后置注入。
  relay.setChat(container.chat);
  container.chat.start();
  container.feed.subscribe((posts) => {
    /* store 已在内部更新；v3.23：实时检测好友新动态 → 通知中心/系统通知 */
    try {
      const contacts = useChatStore.getState().contacts;
      const following = useSocialStore.getState().following;
      const selfDid = useIdentityStore.getState().did;
      const friends = buildFriendDids(contacts, following, selfDid);
      const mine = (selfDid ?? '').toLowerCase();
      const fresh = posts.filter(
        (p) =>
          p.authorDid &&
          p.authorDid.toLowerCase() !== mine &&
          p.scope !== 'private' &&
          friends.has(p.authorDid.toLowerCase()),
      );
      if (fresh.length > 0) checkCircleNotices(fresh);
    } catch {
      /* 通知异常不影响消息流 */
    }
  });
  active = true;
  logger.info('会话已启动：传输 / 内容 / Feed / Chat 服务已装配');
}

/** 锁定时调用：停传输、清内存态，但保留身份与存储。 */
export async function stopSession(): Promise<void> {
  if (!active) return;
  active = false;
  try {
    container.chat.destroy();
  } catch {
    /* noop */
  }
  try {
    relay?.stop();
  } catch {
    /* noop */
  }
  logger.info('会话已停止');
}
